import type { MotifNote, Song, SongSection } from "@engine";

// Six handcrafted melodic-dubstep / future-bass songs, one per level
// theme. Each song supplies a chord progression, lead motifs per
// section, drum-kit colour, and an arrangement plan.
//
// Authoring grammar (kept light by design):
//   - `progression`: ChordSpec[] -- looped across bars within sections.
//   - `motifs[section]`: MotifNote[][] -- one entry per bar inside the
//     section; loop after the last entry. MotifNote.degree counts in
//     scale steps starting at 0 = the song's root. Negative or > 7
//     values jump octaves automatically.
//   - `sections`: order of sections played; once the last finishes we
//     loop back to the first non-intro / non-outro section.
//
// Section conventions (loose, not enforced):
//   intro  : 4 bars, sets harmonic key
//   verse  : 8 bars, calm, progression states the chords
//   build  : 4 bars, energy ramp -- last bar fires riser
//   drop   : 8 bars, full mix, hooks state the lead motif
//   bridge : 4 bars, breakdown, no drums in some patterns
//   outro  : optional fade -- only used at "song end" if we ever loop
//            back to intro

export type SongId = "neon" | "dawn" | "storm" | "void" | "sunset" | "matrix";

// -- helpers --------------------------------------------------------------

// Quick motif builder: list of (step, degree, durationSteps?) tuples.
// Saves a lot of `{ step: 0, degree: 0 }` boilerplate in the song data
// while keeping the underlying MotifNote shape intact.
function m(...notes: Array<[number, number, number?, number?]>): MotifNote[] {
  return notes.map(([step, degree, durationSteps, octave]) => ({
    step,
    degree,
    durationSteps,
    octave,
  }));
}

// Standard-issue 8-bar arrangement that every song reuses (with optional
// outro on top). Keeps the per-song data focused on harmony + melody.
const STANDARD_SECTIONS: SongSection[] = [
  { kind: "intro",  bars: 4 },
  { kind: "verse",  bars: 8 },
  { kind: "build",  bars: 4 },
  { kind: "drop",   bars: 8 },
  { kind: "bridge", bars: 4 },
  { kind: "drop",   bars: 8 },
];

// -- songs ----------------------------------------------------------------

// "neon" -- A minor uplifting future-bass anthem
//   vi - IV - I - V    in scale-degree terms (relative to the song
//   root which is degree 0 = A): the i-VI-III-VII pattern (Am - F - C -
//   G of A natural minor) but with sus2 voicings on the I and V to
//   lift the mood toward future-bass territory.
const neon: Song = {
  id: "neon",
  rootHz: 110, // A2; bass voicing will drop an octave for sub
  scaleId: "minor",
  bpm: 140,
  progression: [
    { degree: 0, quality: "sus2" },   // i sus2
    { degree: 5, quality: "triad" },  // VI
    { degree: 2, quality: "add9" },   // III add9
    { degree: 4, quality: "sus4" },   // V sus4
  ],
  bassRhythm: [0, 6, 8, 14],
  motifs: {
    verse: [
      m([0, 0, 4], [4, 2, 4], [8, 4, 4], [12, 2, 4]),
    ],
    build: [
      m([0, 0, 2], [4, 2, 2], [8, 4, 2], [10, 5, 2], [12, 7, 2], [14, 9, 2]),
    ],
    drop: [
      m([0, 7, 2], [2, 9, 2], [4, 7, 2], [6, 4, 2], [8, 5, 2], [10, 7, 2], [12, 9, 4]),
      m([0, 4, 2], [2, 5, 2], [4, 7, 4], [8, 9, 2], [10, 7, 2], [12, 5, 4]),
    ],
    bridge: [
      m([0, 7, 6], [8, 4, 6]),
    ],
  },
  sections: STANDARD_SECTIONS,
  drumKit: { kickGain: 1, snareGain: 0.75, hatGain: 0.32, kickPunch: 1 },
};

// "dawn" -- Soft, hopeful melodic dubstep in C natural minor ish (with
// dorian colour from the scale def). Slower, dreamier, smaller drop.
const dawn: Song = {
  id: "dawn",
  rootHz: 130.81, // C3
  scaleId: "minor",
  bpm: 132,
  progression: [
    { degree: 0, quality: "add9" },
    { degree: 4, quality: "sus2" },
    { degree: 5, quality: "triad" },
    { degree: 3, quality: "sus2" },
  ],
  bassRhythm: [0, 8, 12],
  motifs: {
    verse: [
      m([0, 4, 4], [6, 5, 2], [8, 4, 4], [12, 2, 4]),
    ],
    build: [
      m([0, 4, 2], [4, 5, 2], [8, 7, 2], [12, 9, 4]),
    ],
    drop: [
      m([0, 9, 4], [4, 7, 2], [6, 5, 2], [8, 4, 4], [12, 2, 4]),
      m([0, 7, 2], [2, 5, 2], [4, 4, 4], [8, 2, 2], [10, 4, 2], [12, 5, 4]),
    ],
    bridge: [
      m([0, 4, 8], [8, 2, 8]),
    ],
  },
  sections: STANDARD_SECTIONS,
  drumKit: { kickGain: 0.85, snareGain: 0.65, hatGain: 0.28, kickPunch: 1.1 },
};

// "storm" -- Dark melodic dubstep in G minor, harder edges, bigger drop.
const storm: Song = {
  id: "storm",
  rootHz: 98, // G2
  scaleId: "minor",
  bpm: 144,
  progression: [
    { degree: 0, quality: "power" },
    { degree: 6, quality: "triad" },   // VII
    { degree: 5, quality: "triad" },   // VI
    { degree: 6, quality: "triad" },   // VII
  ],
  bassRhythm: [0, 4, 8, 10, 14],
  motifs: {
    verse: [
      m([0, 0, 2], [4, 2, 2], [8, 0, 2], [12, 6, 2, -1]),
    ],
    build: [
      m([0, 0, 2], [2, 2, 2], [4, 4, 2], [6, 5, 2], [8, 7, 2], [10, 9, 2], [12, 11, 2], [14, 12, 2]),
    ],
    drop: [
      m([0, 7, 2], [2, 0, 2, 1], [4, 7, 2], [6, 5, 2], [8, 4, 2], [10, 5, 2], [12, 7, 4]),
      m([0, 11, 2], [2, 9, 2], [4, 7, 4], [8, 4, 2], [10, 2, 2], [12, 0, 4]),
    ],
    bridge: [
      m([0, 0, 4], [4, 7, 4], [8, 5, 4], [12, 4, 4]),
    ],
  },
  sections: STANDARD_SECTIONS,
  drumKit: { kickGain: 1.05, snareGain: 0.8, hatGain: 0.34, kickPunch: 0.95 },
};

// "void" -- Cosmic phrygian; ethereal lead, dark mood.
const voidSong: Song = {
  id: "void",
  rootHz: 92.5, // F#2 sharp/flat region
  scaleId: "phrygian",
  bpm: 138,
  progression: [
    { degree: 0, quality: "triad" },
    { degree: 1, quality: "sus2" },   // bII (phrygian's signature)
    { degree: 0, quality: "triad" },
    { degree: 6, quality: "triad" },   // VII
  ],
  bassRhythm: [0, 8],
  motifs: {
    verse: [
      m([0, 4, 6], [8, 1, 6]),
    ],
    build: [
      m([0, 0, 2], [4, 1, 2], [8, 4, 2], [12, 7, 2]),
    ],
    drop: [
      m([0, 7, 2], [2, 8, 2], [4, 7, 2], [6, 4, 2], [8, 1, 2], [10, 4, 2], [12, 7, 4]),
      m([0, 8, 4], [4, 7, 2], [6, 4, 2], [8, 1, 4], [12, 0, 4]),
    ],
    bridge: [
      m([0, 4, 16]),
    ],
  },
  sections: STANDARD_SECTIONS,
  drumKit: { kickGain: 0.95, snareGain: 0.7, hatGain: 0.3, kickPunch: 1.15 },
};

// "sunset" -- Smooth chillstep in D minor / dorian vibe.
const sunset: Song = {
  id: "sunset",
  rootHz: 146.83, // D3
  scaleId: "minor",
  bpm: 128,
  progression: [
    { degree: 5, quality: "sus2" },   // VI
    { degree: 0, quality: "triad" },  // i
    { degree: 4, quality: "triad" },  // V
    { degree: 3, quality: "sus2" },   // IV
  ],
  bassRhythm: [0, 6, 8, 12],
  motifs: {
    verse: [
      m([0, 5, 4], [6, 7, 2], [8, 5, 4], [12, 2, 4]),
    ],
    build: [
      m([0, 2, 2], [4, 4, 2], [8, 5, 2], [12, 7, 4]),
    ],
    drop: [
      m([0, 9, 2], [2, 7, 2], [4, 5, 4], [8, 4, 2], [10, 5, 2], [12, 7, 4]),
      m([0, 4, 2], [2, 5, 2], [4, 7, 4], [8, 5, 2], [10, 2, 2], [12, 0, 4]),
    ],
    bridge: [
      m([0, 5, 8], [8, 4, 8]),
    ],
  },
  sections: STANDARD_SECTIONS,
  drumKit: { kickGain: 0.85, snareGain: 0.7, hatGain: 0.3, kickPunch: 1.05 },
};

// "matrix" -- Stabby, technical, tight. B minor with a 7th flavor.
const matrix: Song = {
  id: "matrix",
  rootHz: 123.47, // B2
  scaleId: "minor",
  bpm: 142,
  progression: [
    { degree: 0, quality: "seventh" },
    { degree: 3, quality: "seventh" }, // iv
    { degree: 6, quality: "triad" },   // VII
    { degree: 2, quality: "sus2" },    // III
  ],
  bassRhythm: [0, 4, 8, 12],
  motifs: {
    verse: [
      m([0, 0, 2], [4, 2, 2], [8, 4, 2], [12, 2, 2]),
    ],
    build: [
      m([0, 0, 1], [2, 2, 1], [4, 4, 1], [6, 5, 1], [8, 7, 1], [10, 9, 1], [12, 7, 1], [14, 9, 2]),
    ],
    drop: [
      m([0, 7, 1], [1, 9, 1], [2, 7, 1], [3, 11, 1], [4, 7, 2], [8, 4, 2], [10, 7, 2], [12, 9, 4]),
      m([0, 9, 2], [2, 11, 2], [4, 9, 2], [6, 7, 2], [8, 4, 4], [12, 2, 4]),
    ],
    bridge: [
      m([0, 4, 4], [4, 5, 4], [8, 7, 4], [12, 4, 4]),
    ],
  },
  sections: STANDARD_SECTIONS,
  drumKit: { kickGain: 0.95, snareGain: 0.75, hatGain: 0.36, kickPunch: 0.85 },
};

export const SONGS: Record<SongId, Song> = {
  neon,
  dawn,
  storm,
  void: voidSong,
  sunset,
  matrix,
};

export function getSong(id: SongId): Song {
  return SONGS[id] ?? SONGS.neon;
}
